import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuthStore } from "@/stores/adminAuthStore";
import { toastError } from "@/shared/lib/toast";
import { AuthTextField, AuthPasswordField } from "@/shared/components/AuthField";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { validateFields } from "@/shared/lib/validators";

export function AdminSignInPage() {
  const signIn = useAdminAuthStore((s) => s.signIn);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validateFields(
      { email, password },
      {
        email: [
          { required: true, message: "Email is required" },
          { type: "email", message: "Enter a valid email address" },
        ],
        password: [{ required: true, message: "Password is required" }],
      },
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setLoading(true);
    const result = await signIn(email, password);
    setLoading(false);

    if (result.success) {
      navigate("/admin", { replace: true });
      return;
    }

    toastError(result.error ?? "Unable to sign in.");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-white">

      {/* ── Left panel — logo + form ── */}
      <div className="flex w-[55%] flex-col">

        {/* Logo — pinned at top */}
        <div className="flex shrink-0 items-center gap-3 pl-14 pr-4 pt-8 pb-4">
          <img src="/logo.png" alt="MedSyncIQ" className="h-10 w-10 rounded-lg object-contain" />
          <div className="flex flex-col gap-0">
            <h1
              className="text-lg tracking-tight text-[#0A2947]"
              style={{ fontWeight: 900, letterSpacing: "-0.02em" }}
            >
              MedSyncIQ
            </h1>
            <p className="text-[11px] font-medium leading-tight text-[#4B5563]">
              Admin Portal
            </p>
          </div>
        </div>

        {/* Form slot — top-aligned */}
        <div className="flex flex-1 flex-col overflow-y-auto pr-8 pb-8 pt-6">
          <div className="w-full max-w-sm mx-auto ml-60">
            <h2 className="!font-normal text-[28px] leading-tight text-[#0A2947]">
              Welcome Back, <span className="font-extrabold tracking-tight">Admin</span>
            </h2>
            <p className="!mt-2 !mb-8 text-sm text-[#9CA3AF]">
              Sign in with your administrator credentials
            </p>

            <form onSubmit={handleSubmit}>
              <AuthTextField
                label="Email"
                placeholder="admin@medsynciq.com"
                value={email}
                onChange={setEmail}
                error={errors.email}
              />

              <AuthPasswordField
                label="Password"
                placeholder="Enter your password"
                value={password}
                onChange={setPassword}
                error={errors.password}
                className="mb-7"
              />

              <AuthSubmitButton loading={loading}>Sign in</AuthSubmitButton>
            </form>

            <p className="!mt-10 text-center text-sm text-[#9CA3AF]">
              Access is restricted to registered administrator accounts.
            </p>
          </div>
        </div>
      </div>

      {/* ── Right panel — image ── */}
      <div className="flex flex-1 p-4">
        <div className="relative flex-1 overflow-hidden rounded-3xl">
          <img
            src="/Image1.png"
            alt="Healthcare professionals"
            className="h-full w-full object-cover object-center"
          />
        </div>
      </div>

    </div>
  );
}
